import { formatDate } from '@helpers/date';

/**
 * The shape and the wording shared by the two agreement surfaces.
 *
 * There are two views of one object: `/agreements` lists every signed document across clients,
 * and the Agreements tab on an employer workspace lists one client's. They were written
 * separately and had drifted into agreeing on almost nothing — two copies of `fmt`, two copies
 * of `fmtSize`, two row interfaces naming the same fields, and two status colour maps that
 * rendered the same value in different colours.
 *
 * The colours are now `CRMStatusChip`'s. What is left that genuinely belongs to agreements
 * rather than to the console lives here, so a change to how a signed date reads happens once.
 */

export interface AgreementRow {
  _id: string;
  employerId: string;
  title?: string;
  fileName?: string;
  fileSizeBytes?: number;
  signedAt?: string;
  expiresAt?: string;
  status?: string;
  visibleToEmployer?: boolean;
  uploadedAt?: string;
}

/** A date on an agreement. Never relative: paperwork is referred to by its date, not its age. */
export const fmtDate = (d?: string) => formatDate(d);

/** File size, to the roundest unit that stays honest. */
export const fmtSize = (bytes?: number) =>
  !bytes
    ? '-'
    : bytes < 1024 * 1024
      ? `${Math.max(1, Math.round(bytes / 1024))} KB`
      : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

const KIND_BY_EXT: Record<string, string> = { pdf: 'PDF', doc: 'Word', docx: 'Word' };

/**
 * What the file is, in words: "PDF · 1.2 MB". Never the raw file name.
 *
 * The name under an agreement's title used to be `fileName` verbatim, and for records filed
 * against an object key it was a 24-character hex id that the table clipped mid-string. That told
 * a reader nothing and looked like a fault. The kind comes from the extension of the name, or of
 * the stored key when the name has none.
 */
export const docMeta = (row: { fileName?: string; s3Key?: string; fileSizeBytes?: number }) => {
  const extOf = (s?: string) => {
    const m = s ? /\.([a-z0-9]{2,5})$/i.exec(s) : null;
    return m ? m[1].toLowerCase() : '';
  };
  const ext = extOf(row.fileName) || extOf(row.s3Key);
  const kind = KIND_BY_EXT[ext] ?? 'Document';
  return row.fileSizeBytes ? `${kind} · ${fmtSize(row.fileSizeBytes)}` : kind;
};

/** "Expires 3 Mar 2027" or "Expired 3 Mar 2026", against today. */
export const expiryText = (expiresAt?: string, now: Date = new Date()) => {
  if (!expiresAt) return null;
  const past = new Date(expiresAt).getTime() < now.getTime();
  return `${past ? 'Expired' : 'Expires'} ${fmtDate(expiresAt)}`;
};
